import { FunctionComponent, useState, FormEvent, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import {
  sendUserEmailVerification,
  signUserUp,
  upsertUser,
} from "../services/firebase";
import { contextSignInUserAtom } from "../state/atoms/user";
import { LoginSignupInputs, LoginSignupSection, UserMessages } from "../types";
import Loader from "./Loader";
import Notification from "./Notification";
import Spinner from "./Spinner";

const Signup: FunctionComponent<{
  signupSection?: LoginSignupSection;
  loginSignupInputs?: LoginSignupInputs;
  userMessages?: UserMessages;
}> = ({ signupSection, loginSignupInputs, userMessages }) => {
  const navigate = useNavigate();

  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpConfirmPassword, setSignUpConfirmPassword] = useState("");
  const [signUpDisplayName, setSignUpDisplayName] = useState("");
  const [signUpPromoCode, setSignUpPromoCode] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const signedInUser = useRecoilValue(contextSignInUserAtom);

  useEffect(() => {
    setSignUpEmail("");
    setSignUpPassword("");
    setIsSubmitted(false);
  }, []);

  useEffect(() => {
    if (signedInUser) navigate("/my-personal-area");
  }, [signedInUser, navigate]);

  if (!signupSection || !loginSignupInputs) return <Loader />;
  const {
    emailLabel,
    emailPlaceholder,
    passwordLabel,
    passwordPlaceholder,
    confirmPassword,
    displayNameLabel,
    displayNamePlaceholder,
    promoCodeLabel,
    promoCodePlaceholder,
    loginCta,
    signupCta,
  } = loginSignupInputs;

  const { formSection } = signupSection;

  if (!formSection) return <Loader />;

  const { isUserSigned, title } = formSection;

  const handleSignUp = async (ev: FormEvent) => {
    ev.preventDefault();
    if (
      !signUpEmail ||
      !signUpPassword ||
      !signUpDisplayName ||
      signUpPassword !== signUpConfirmPassword
    ) {
      setIsErrored(true);
      return;
    }

    try {
      setIsSubmitted(false);
      setIsSubmitting(true);
      const { user } = await signUserUp(signUpEmail.trim(), signUpPassword);
      if (!user) {
        setIsErrored(true);
        return;
      }

      const { emailVerified, uid, email } = user;
      if (!uid || !email) {
        setIsErrored(true);
        return;
      }

      await upsertUser({
        uid,
        email,
        emailVerified,
        isTeacher: false,
        isAdmin: false,
        isTestUser: false,
        displayName: signUpDisplayName,
        promoCode: signUpPromoCode,
        participatingInGroups: [],
        courses: [],
      });

      await sendUserEmailVerification(user);
    } catch (err) {
      setIsErrored(true);
    } finally {
      setIsSubmitted(true);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="user-data-page clearfix d-lg-flex text-start">
      <div className="illustration-wrapper d-flex align-items-center justify-content-around flex-column">
        <div className="illustration-holder">
          <img src="images/assets/ils_08.svg" alt="" className="illustration" />
          <img
            src="images/assets/ils_08.1.svg"
            alt=""
            className="shapes shape-one"
          />
          <img
            src="images/assets/ils_08.2.svg"
            alt=""
            className="shapes shape-two"
          />
        </div>
      </div>

      <div className="form-wrapper">
        <form
          action="#"
          className="user-data-form mt-30"
          onSubmit={handleSignUp}
        >
          <h2>{title}</h2>
          <p className="header-info pt-30">
            {isUserSigned} <Link to="/login">{loginCta}</Link>
          </p>
          <div className="row">
            <div className="col-12 d-flex justify-content-center">
              <Spinner isVisible={isSubmitting} size={"md"} />
            </div>
            {isSubmitted && (
              <div className="col-12 mb-4">
                <Notification
                  isVisible={isSubmitted}
                  message={
                    !isErrored
                      ? userMessages?.successMessage
                      : userMessages?.errorMessage
                  }
                  type={!isErrored ? "info" : "error"}
                />
              </div>
            )}
            <div className="col-12">
              <div className="input-group-meta mb-50">
                <label>{emailLabel}</label>
                <input
                  type="email"
                  placeholder={emailPlaceholder}
                  autoComplete="email"
                  onChange={(ev) =>
                    setSignUpEmail(ev.currentTarget.value.trim())
                  }
                />
              </div>
            </div>
            <div className="col-12">
              <div className="input-group-meta mb-50">
                <label>{passwordLabel}</label>
                <input
                  type="password"
                  placeholder={passwordPlaceholder}
                  className="pass_log_id"
                  autoComplete="off"
                  onChange={(ev) =>
                    setSignUpPassword(ev.currentTarget.value.trim())
                  }
                />
              </div>
            </div>
            <div className="col-12">
              <div className="input-group-meta mb-50">
                <label>{confirmPassword}</label>
                <input
                  type="password"
                  autoComplete="off"
                  placeholder={passwordPlaceholder}
                  onChange={(ev) =>
                    setSignUpConfirmPassword(ev.currentTarget.value.trim())
                  }
                />
              </div>
            </div>
            <div className="col-12">
              <div className="input-group-meta mb-50">
                <label>{displayNameLabel}</label>
                <input
                  autoComplete="off"
                  placeholder={displayNamePlaceholder}
                  className="pass_log_id"
                  onChange={(ev) =>
                    setSignUpDisplayName(ev.currentTarget.value.trim())
                  }
                />
              </div>
            </div>
            <div className="col-12">
              <div className="input-group-meta mb-15">
                <label>{promoCodeLabel}</label>
                <input
                  autoComplete="off"
                  placeholder={promoCodePlaceholder}
                  className="pass_log_id"
                  onChange={(ev) =>
                    setSignUpPromoCode(ev.currentTarget.value.trim())
                  }
                />
              </div>
            </div>
            <div className="col-12">
              <button className="app-theme-btn mt-30 mb-50">{signupCta}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
